import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AdminDispatch() {
  const [unassigned, setUnassigned] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [picked, setPicked] = useState(null);
  const [assignDriver, setAssignDriver] = useState(null);
  const [chosenVehicle, setChosenVehicle] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [u, d, v] = await Promise.all([
        api.get("/admin/bookings/unassigned"),
        api.get("/admin/drivers/available"),
        api.get("/admin/vehicles/available"),
      ]);
      setUnassigned(u.data.items || []);
      setDrivers(d.data || []);
      setVehicles(v.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 20000);
    return () => clearInterval(i);
  }, [refresh]);

  const startAssign = (driver) => {
    if (!picked) return;
    setAssignDriver(driver);
    const match = vehicles.find((v) => v.category === picked.vehicle_category);
    setChosenVehicle(match || vehicles[0] || null);
  };

  const confirmAssign = async () => {
    if (!picked || !assignDriver || !chosenVehicle) return;
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/admin/bookings/${picked.id}/assign`, {
        driver_id: assignDriver.id,
        vehicle_id: chosenVehicle.id,
      });
      setPicked(null);
      setAssignDriver(null);
      setChosenVehicle(null);
      await refresh();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-dispatch" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Dispatch</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tighter">Pending reservations</h1>
        </div>
        <button
          onClick={refresh}
          data-testid="dispatch-refresh-btn"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#8E9AA6] hover:text-white"
        >
          <RefreshCw size={14} strokeWidth={1.5} /> Refresh
        </button>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Unassigned */}
        <div className="bg-[#121214]/60 border border-white/10">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Unassigned · {unassigned.length}</div>
          </div>
          <div data-testid="dispatch-unassigned-list" className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
            {unassigned.length === 0 && (
              <div className="px-5 py-10 text-center text-xs text-[#8E9AA6] uppercase tracking-[0.3em]">
                All bookings assigned
              </div>
            )}
            {unassigned.map((b) => (
              <button
                key={b.id}
                onClick={() => setPicked(b)}
                data-testid={`dispatch-unassigned-${b.id}`}
                className={`w-full text-left px-5 py-4 transition-colors ${
                  picked?.id === b.id ? "bg-white/[0.05] border-l-2 border-white" : "border-l-2 border-transparent hover:bg-white/[0.02]"
                }`}
              >
                <div className="flex justify-between items-baseline">
                  <div className="font-serif text-lg">{new Date(b.pickup_datetime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">{b.vehicle_category.replace(/_/g, " ")}</div>
                </div>
                <div className="text-sm font-light mt-1 truncate">
                  {b.pickup_address}
                  {b.dropoff_address && <span className="text-[#8E9AA6]"> → {b.dropoff_address}</span>}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] mt-1">
                  {b.client_name} · {b.service_type.replace(/_/g, " ")}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Drivers */}
        <div className="bg-[#121214]/60 border border-white/10">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Available chauffeurs · {drivers.length}</div>
          </div>
          <div data-testid="dispatch-drivers-list" className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
            {drivers.length === 0 && (
              <div className="px-5 py-10 text-center text-xs text-[#8E9AA6] uppercase tracking-[0.3em]">
                No chauffeurs on duty
              </div>
            )}
            {drivers.map((d) => (
              <button
                key={d.id}
                disabled={!picked}
                onClick={() => startAssign(d)}
                data-testid={`dispatch-driver-${d.id}`}
                className="w-full text-left px-5 py-4 transition-colors hover:bg-white/[0.03] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between"
              >
                <div>
                  <div className="font-serif text-lg">{d.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] mt-0.5">
                    {d.email} · load {d.active_load}
                  </div>
                </div>
                <ChevronRight size={16} className="text-[#8E9AA6]" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      {assignDriver && picked && (
        <div data-testid="dispatch-assign-modal" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0B0B0C] border border-white/15 w-full max-w-md p-6">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Assign</div>
                <div className="font-serif text-xl mt-1">{assignDriver.name}</div>
              </div>
              <button onClick={() => setAssignDriver(null)} data-testid="dispatch-modal-close" className="text-[#8E9AA6] hover:text-white p-1">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="mb-5 border border-white/10 bg-[#121214]/60 p-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Trip</div>
              <div className="text-sm font-light">{picked.pickup_address}</div>
              {picked.dropoff_address && <div className="text-sm text-[#8E9AA6]">→ {picked.dropoff_address}</div>}
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] mt-2">Requested: {picked.vehicle_category.replace(/_/g, " ")}</div>
            </div>

            <div className="mb-4">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-2">Vehicle</label>
              <select
                value={chosenVehicle?.id || ""}
                onChange={(e) => setChosenVehicle(vehicles.find((v) => v.id === e.target.value))}
                data-testid="dispatch-modal-vehicle-select"
                className="w-full bg-[#121214] border border-white/15 text-white px-3 py-2.5 text-sm focus:border-white focus:outline-none"
              >
                <option value="">Select vehicle…</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.make} {v.model} · {v.category.replace(/_/g, " ")} · {v.license_plate}
                  </option>
                ))}
              </select>
              {chosenVehicle && chosenVehicle.category !== picked.vehicle_category && (
                <div className="text-[11px] text-amber-300 mt-2">
                  Category mismatch — chosen {chosenVehicle.category.replace(/_/g, " ")}, requested {picked.vehicle_category.replace(/_/g, " ")}.
                </div>
              )}
            </div>

            {err && <div className="text-xs text-red-400 mb-3">{err}</div>}

            <button
              onClick={confirmAssign}
              disabled={!chosenVehicle || submitting}
              data-testid="dispatch-modal-confirm"
              className="w-full bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-6 py-3 text-xs uppercase tracking-[0.25em]"
            >
              {submitting ? "Assigning…" : "Confirm assignment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
